/**
 * DEMO DATA - docasne.
 *
 * Portal API (dev.realico.sk) je bez pristupu / prazdna DB - /api/v1/locations/search
 * lokalne nevrati pouzitelne data. Kym nepridu realne data z API, seedujem tu
 * zakladnu sadu SK lokalit (kraje / okresy / mesta a obce), aby sa dal field
 * "Vyberte miesto" v klasickom filtri odskusat a polishovat.
 *
 * API ma vzdy prednost - fixture sa pouzije len ako fallback (pozri
 * useListingsClassicFilter.startSearchLocation). Rovnaka konvencia ako
 * DEMO_TOP_LISTINGS.
 *
 * ZMAZAT hned ako pojde locations API.
 */
import type { LocationDto } from '@/api/generated/model/locationDto';
import { LocationDtoType } from '@/api/generated/model/locationDtoType';

/** Diakritiku zhodi a znizi - pre slug aj pre hladanie bez diakritiky. */
export function foldDiacritics(value: string): string {
    // NFD rozlozi pismeno na zaklad + kombinujuci diakriticky znak (U+0300-U+036F),
    // tie odfiltrujeme cez code-point (bez neviditelnych znakov v zdrojaku).
    return Array.from(value.normalize('NFD'))
        .filter((ch) => {
            const code = ch.codePointAt(0) ?? 0;
            return code < 0x300 || code > 0x36f;
        })
        .join('')
        .toLowerCase();
}

function slug(name: string): string {
    return foldDiacritics(name)
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

type Seed = {
    name: string;
    type: LocationDtoType;
    region?: string;
    district?: string;
};

/* Kraje, vybrane okresy a najvacsie mesta/obce. Zamerne skratena, ale
   dost siroka sada, aby bezne hladania (bra, kos, nit, zi, presov...) nieco vratili. */
const SEED: Seed[] = [
    // Kraje
    { name: 'Bratislavský kraj', type: LocationDtoType.region, region: 'ba' },
    { name: 'Trnavský kraj', type: LocationDtoType.region, region: 'tt' },
    { name: 'Trenčiansky kraj', type: LocationDtoType.region, region: 'tn' },
    { name: 'Nitriansky kraj', type: LocationDtoType.region, region: 'nr' },
    { name: 'Žilinský kraj', type: LocationDtoType.region, region: 'za' },
    { name: 'Banskobystrický kraj', type: LocationDtoType.region, region: 'bb' },
    { name: 'Prešovský kraj', type: LocationDtoType.region, region: 'po' },
    { name: 'Košický kraj', type: LocationDtoType.region, region: 'ke' },

    // Okresy (vyber)
    { name: 'Okres Bratislava I', type: LocationDtoType.district, region: 'ba', district: 'ba1' },
    { name: 'Okres Bratislava II', type: LocationDtoType.district, region: 'ba', district: 'ba2' },
    { name: 'Okres Bratislava III', type: LocationDtoType.district, region: 'ba', district: 'ba3' },
    { name: 'Okres Pezinok', type: LocationDtoType.district, region: 'ba', district: 'pk' },
    { name: 'Okres Senec', type: LocationDtoType.district, region: 'ba', district: 'sc' },
    { name: 'Okres Trnava', type: LocationDtoType.district, region: 'tt', district: 'tt' },
    { name: 'Okres Trenčín', type: LocationDtoType.district, region: 'tn', district: 'tn' },
    { name: 'Okres Nitra', type: LocationDtoType.district, region: 'nr', district: 'nr' },
    { name: 'Okres Žilina', type: LocationDtoType.district, region: 'za', district: 'za' },
    { name: 'Okres Martin', type: LocationDtoType.district, region: 'za', district: 'mt' },
    { name: 'Okres Banská Bystrica', type: LocationDtoType.district, region: 'bb', district: 'bb' },
    { name: 'Okres Prešov', type: LocationDtoType.district, region: 'po', district: 'po' },
    { name: 'Okres Poprad', type: LocationDtoType.district, region: 'po', district: 'pp' },
    { name: 'Okres Košice I', type: LocationDtoType.district, region: 'ke', district: 'ke1' },
    { name: 'Okres Košice II', type: LocationDtoType.district, region: 'ke', district: 'ke2' },

    // Mesta a obce
    { name: 'Bratislava', type: LocationDtoType.municipality, region: 'ba', district: 'ba1' },
    { name: 'Bratislava - Staré Mesto', type: LocationDtoType.municipality, region: 'ba', district: 'ba1' },
    { name: 'Bratislava - Ružinov', type: LocationDtoType.municipality, region: 'ba', district: 'ba2' },
    { name: 'Bratislava - Petržalka', type: LocationDtoType.municipality, region: 'ba', district: 'ba5' },
    { name: 'Bratislava - Nové Mesto', type: LocationDtoType.municipality, region: 'ba', district: 'ba3' },
    { name: 'Pezinok', type: LocationDtoType.municipality, region: 'ba', district: 'pk' },
    { name: 'Senec', type: LocationDtoType.municipality, region: 'ba', district: 'sc' },
    { name: 'Trnava', type: LocationDtoType.municipality, region: 'tt', district: 'tt' },
    { name: 'Piešťany', type: LocationDtoType.municipality, region: 'tt', district: 'ps' },
    { name: 'Dunajská Streda', type: LocationDtoType.municipality, region: 'tt', district: 'ds' },
    { name: 'Trenčín', type: LocationDtoType.municipality, region: 'tn', district: 'tn' },
    { name: 'Prievidza', type: LocationDtoType.municipality, region: 'tn', district: 'pd' },
    { name: 'Nitra', type: LocationDtoType.municipality, region: 'nr', district: 'nr' },
    { name: 'Nové Zámky', type: LocationDtoType.municipality, region: 'nr', district: 'nz' },
    { name: 'Levice', type: LocationDtoType.municipality, region: 'nr', district: 'lv' },
    { name: 'Žilina', type: LocationDtoType.municipality, region: 'za', district: 'za' },
    { name: 'Martin', type: LocationDtoType.municipality, region: 'za', district: 'mt' },
    { name: 'Liptovský Mikuláš', type: LocationDtoType.municipality, region: 'za', district: 'lm' },
    { name: 'Banská Bystrica', type: LocationDtoType.municipality, region: 'bb', district: 'bb' },
    { name: 'Zvolen', type: LocationDtoType.municipality, region: 'bb', district: 'zv' },
    { name: 'Prešov', type: LocationDtoType.municipality, region: 'po', district: 'po' },
    { name: 'Poprad', type: LocationDtoType.municipality, region: 'po', district: 'pp' },
    { name: 'Bardejov', type: LocationDtoType.municipality, region: 'po', district: 'bj' },
    { name: 'Košice', type: LocationDtoType.municipality, region: 'ke', district: 'ke1' },
    { name: 'Michalovce', type: LocationDtoType.municipality, region: 'ke', district: 'mi' },
    { name: 'Spišská Nová Ves', type: LocationDtoType.municipality, region: 'ke', district: 'sn' },
];

export const DEMO_LOCATIONS: LocationDto[] = SEED.map((s, idx) => ({
    id: `demo-loc-${idx}`,
    name: s.name,
    type: s.type,
    // kontrakt: nullable string je vygenerovany ako { [key: string]: unknown } | null (chyba `type`
    // v @ApiProperty) - cast len na typ, hodnota je realny slug kraja/okresu. Blocker pre Lubosa.
    regionId: (s.region ?? null) as unknown as LocationDto['regionId'],
    districtId: (s.district ?? null) as unknown as LocationDto['districtId'],
    slug: slug(s.name),
}));

/** Fulltext nad menom bez ohladu na diakritiku a velkost pismen. */
export function filterDemoLocations(query: string, limit = 12): LocationDto[] {
    const q = foldDiacritics(query.trim());
    if (!q) return [];
    return DEMO_LOCATIONS.filter((loc) => foldDiacritics(loc.name).includes(q)).slice(0, limit);
}

/** Vyriesi vybrane lokality zo slugov (URL param locations). */
export function demoLocationsBySlugs(slugs: string[]): LocationDto[] {
    const set = new Set(slugs.map((s) => s.trim()).filter(Boolean));
    return DEMO_LOCATIONS.filter((loc) => set.has(loc.slug));
}
